<template>
    <BaseCard padding="lg" class="flex flex-col gap-2.5">
        <p v-if="headline" class="nadia-subtitle text-ink m-0">{{ headline }}</p>

        <BaseCard v-if="hasChart" theme="cream" radius="lgr" class="flex flex-col gap-6">
            <CampSegmentedBar v-for="row in chartRows" :key="row.key" :label="row.label" :distribution="row.distribution" :avg="row.avg" />
            <CampLegend />
        </BaseCard>

        <BaseCard v-if="insight?.strengths?.length" theme="cream" radius="lgr" class="flex flex-col gap-2.5">
            <div class="flex items-center gap-2 body-small-semibold text-ink">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path class="stroke-ink-inverse" d="M8 11.5V4.8 M5.2 7.6 L8 4.6 L10.8 7.6" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Strengths
            </div>
            <HorizontalDivider as="hr" spacing="none" />
            <div v-for="(entry, i) in insight.strengths" :key="`s-${i}`" class="flex flex-col gap-1">
                <p class="body-small-semibold text-ink m-0">{{ entry.label }}</p>
                <ul class="pl-5 m-0">
                    <li class="body-small text-ink list-disc">{{ entry.detail }}</li>
                </ul>
            </div>
        </BaseCard>

        <BaseCard v-if="insight?.opportunities?.length" theme="cream" radius="lgr" class="flex flex-col gap-2.5">
            <div class="flex items-center gap-2 body-small-semibold text-ink">
                <svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" class="w-4 h-4 shrink-0" aria-hidden="true">
                    <circle cx="8" cy="8" r="8" fill="currentColor" />
                    <path class="stroke-ink-inverse" d="M5.2 10.8 L10.8 5.2 M6.4 5.2 L10.8 5.2 L10.8 9.6" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                Opportunities
            </div>
            <HorizontalDivider as="hr" spacing="none" />
            <div v-for="(entry, i) in insight.opportunities" :key="`o-${i}`" class="flex flex-col gap-1">
                <p class="body-small-semibold text-ink m-0">{{ entry.label }}</p>
                <ul class="pl-5 m-0">
                    <li class="body-small text-ink list-disc">{{ entry.detail }}</li>
                </ul>
            </div>
        </BaseCard>

        <div v-if="insight?.summary" class="flex items-stretch gap-2.5 p-2.5">
            <span class="w-1 rounded-full shrink-0 bg-insight-accent-dot" aria-hidden="true"></span>
            <p class="body-small text-ink m-0">{{ insight.summary }}</p>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import BaseCard from "~vue/components/BaseCard.vue";
import HorizontalDivider from "~vue/components/HorizontalDivider.vue";
import CampLegend from "~vue/pages/reflections/CampLegend.vue";
import CampSegmentedBar from "~vue/pages/reflections/CampSegmentedBar.vue";
import { computed } from "vue";

interface ChartRow {
    key: string;
    label: string;
    distribution: number[];
    avg: number | null;
}

interface InsightEntry {
    label: string;
    detail: string;
}

interface Insight {
    strengths?: InsightEntry[];
    opportunities?: InsightEntry[];
    summary?: string;
}

interface Props {
    headline?: string;
    chartRows?: ChartRow[];
    insight?: Insight | null;
}

const props = defineProps<Props>();

const hasChart = computed(() => (props.chartRows?.length ?? 0) > 0);
</script>
